// Author: Tejal Komb

import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
    Box,
    Text,
    Grid,
    GridItem,
    Button,
    Icon,
    Flex,
    Heading,
    Divider,
    Skeleton,
    VStack,
    SkeletonCircle,
    useColorModeValue,
    useTheme, 
} from '@chakra-ui/react';
import { FaEdit, FaTrash } from 'react-icons/fa';

const ViewInternshipDetails = () => {
    const { isAdmin } = useSelector((state) => state.user);
    const [internships, setInternships] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const theme = useTheme(); 

  
    const lightBlueBg = useColorModeValue('blue.50', 'blue.900');
    const lightBlueHeading = useColorModeValue('blue.600', 'blue.300');
    const lightBlueText = useColorModeValue('blue.500', 'blue.400');
    const cardBg = useColorModeValue('white', 'gray.800');
    const cardBorder = useColorModeValue('gray.200', 'gray.700');
    const buttonEditBg = useColorModeValue('blue.200', 'blue.700');
    const buttonEditColor = useColorModeValue('blue.700', 'blue.200');
    const buttonDeleteBg = useColorModeValue('red.200', 'red.700');
    const buttonDeleteColor = useColorModeValue('red.700', 'red.200');

    const getInternships = async () => {
        setIsLoading(true);
        try {
            const accessToken = window.sessionStorage.getItem('accessToken');
            const { data } = await axios.get(`https://gyanbackend.aim4u.co.in/super/view-internships/`, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            });
            setInternships(data);
        } catch (error) {
            console.error("Error fetching internships:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const deleteInternship = async (id) => {
        if (window.confirm("Are you sure you want to delete this internship?")) {
            try {
                const accessToken = window.sessionStorage.getItem('accessToken');
                await axios.delete(`https://gyanbackend.aim4u.co.in/super/delete-internship/${id}/`, {
                    headers: {
                        'Authorization': `Bearer ${accessToken}`
                    }
                });
                getInternships();
            } catch (error) {
                console.error("Error deleting internship:", error);
            }
        }
    };

    useEffect(() => {
        getInternships();
    }, []);

    if (isLoading) {
        return (
            <Box bg={lightBlueBg} minH="100vh" py={8} px={{ base: 4, md: 8, lg: 16 }}>
                <Heading textAlign="center" mb={8} color={lightBlueHeading}>Loading Internship Listings...</Heading>
                <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={6}>
                    {Array.from({ length: 3 }).map((_, index) => (
                        <GridItem key={index}>
                            <VStack spacing={4} bg={cardBg} borderRadius="md" boxShadow="sm" p={6} border="1px solid" borderColor={cardBorder}>
                                <Skeleton height="20px" width="80%" />
                                <Skeleton height="15px" width="60%" />
                                <Skeleton height="15px" />
                                <Skeleton height="15px" />
                                <Flex justify="flex-end">
                                    <SkeletonCircle size="30px" mr={2} />
                                    <SkeletonCircle size="30px" />
                                </Flex>
                            </VStack>
                        </GridItem>
                    ))}
                </Grid>
            </Box>
        );
    }

    return (
        <Box bg= "#e8f3fd" minH="100vh" py={8} px={{ base: 4, md: 8, lg: 16 }}>
            <Flex justify="center" mb={8}>
                <Heading fontSize="35px" fontWeight="bold" ml="80px" mb="20px" color="green.500">
                    Internship Listings
                </Heading>
            </Flex>

            {internships.length === 0 ? (
                <Text textAlign="center" fontSize="lg" color={lightBlueText}>No internship listings available.</Text>
            ) : (
                <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={6}>
                    {internships.map((internship) => (
                        <GridItem key={internship.id}>
                        <Box
                            bg={cardBg}
                            borderRadius="md"
                            boxShadow="md"
                            p={6}
                            transition="all 0.3s ease-in-out"
                            _hover={{ transform: "scale(1.02)", boxShadow: "lg" }}
                            border="1px solid"
                            borderColor={cardBorder}
                        >
                            <Heading size="md" mb={3} color={lightBlueHeading}>
                                {internship.Internship_title}
                            </Heading>
                            
                            <Divider mb={4} />
                            <VStack spacing={2} align="start">
                                    <Flex align="center">
                                        <Text fontWeight="semibold" as="span" color="gray.700" mr={2}>Eligibility:</Text>
                                        <Text color="gray.600">{internship.eligibility_criteria}</Text>
                                    </Flex>
                                    <Flex align="center">
                                        <Text fontWeight="semibold" as="span" color="gray.700" mr={2}>Duration:</Text>
                                        <Text color="gray.600">{internship.duration_weeks} weeks</Text>
                                    </Flex>
                                    <Flex align="center">
                                        <Text fontWeight="semibold" as="span" color="gray.700" mr={2}>Fees:</Text>
                                        <Text color="gray.600">₹{internship.fees}</Text>
                                    </Flex>
                                    <Flex align="center">
                                        <Text fontWeight="semibold" as="span" color="gray.700" mr={2}>Internship Type:</Text>
                                        <Text color="gray.600">{internship.Internship_type}</Text>
                                    </Flex>
                                    <Flex align="center">
                                        <Text fontWeight="semibold" as="span" color="gray.700" mr={2}>Key Skills:</Text>
                                        <Text color="gray.600">{internship.key_skills}</Text>
                                    </Flex>
                                    <Flex align="center">
                                        <Text fontWeight="semibold" as="span" color="gray.700" mr={2}>LOR:</Text>
                                        <Text color="gray.600">{internship.lor ? "Yes" : "No"}</Text>
                                    </Flex>
                                    <Flex align="center">
                                        <Text fontWeight="semibold" as="span" color="gray.700" mr={2}>Paper Writing:</Text>
                                        <Text color="gray.600">{internship.paper_writing ? "Yes" : "No"}</Text>
                                    </Flex>
                                    <Flex align="center">
                                        <Text fontWeight="semibold" as="span" color="gray.700" mr={2}>Project Count:</Text>
                                        <Text color="gray.600">{internship.project_count}</Text>
                                    </Flex>
                                </VStack>
                            <Flex mt={6} justify="flex-end">
                                <Link to={`/updateDetails/${internship.id}`}>
                                    <Button
                                        size="sm"
                                        leftIcon={<Icon as={FaEdit} />}
                                        bg={buttonEditBg}
                                        color={buttonEditColor}
                                        _hover={{ bg: theme.colors.blue[300] }}
                                        mr={2}
                                    >
                                        Edit
                                    </Button>
                                </Link>
                                <Button
                                    size="sm"
                                    leftIcon={<Icon as={FaTrash} />}
                                    bg={buttonDeleteBg}
                                    color={buttonDeleteColor}
                                    _hover={{ bg: theme.colors.red[300] }}
                                    onClick={() => deleteInternship(internship.id)}
                                >
                                    Delete
                                </Button>
                            </Flex>
                        </Box>
                    </GridItem>
                    ))}
                </Grid>
            )}
        </Box>
    );
    
};

export default ViewInternshipDetails;